import React, { useState, useEffect, useCallback } from "react";

import { MasterDetailLayout, ConfirmDialog, ContentLoader, Breadcrumb } from "../../../components/Common";
import { useWorkouts } from "./hooks/useWorkouts";
import WorkoutSidebar from "./components/WorkoutSidebar";
import WorkoutEditor from "./components/WorkoutEditor";
import { initialWorkoutState, templateWorkoutState } from "../../../models/workout";

const WorkoutsPage = () => {

    const {
        selectedDate,
        workouts,
        loading,
        isInitialLoad,
        saving,
        error,
        handleDateChange,
        handleSave,
        handleDelete
    } = useWorkouts();

    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const [isDirty, setIsDirty] = useState(false);



    // Seleção inicial automática dependente da data
    useEffect(() => {
        if (!selectedWorkout && workouts.length > 0 && !loading) {
            setSelectedWorkout(workouts[0]);
        }
    }, [workouts, loading, selectedWorkout]);

    const handleSelectWorkout = useCallback(async (workout) => {
        if (selectedWorkout?.id === workout.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações de treino não salvas. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedWorkout(workout);
        setIsDirty(false);
    }, [selectedWorkout, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar um novo treino?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedWorkout(initialWorkoutState(selectedDate));
        setIsDirty(false);
    }, [isDirty, selectedDate]);

    const onSave = async (formData) => {
        const success = await handleSave(formData);
        if (success) {
            setIsDirty(false);
            // After saving a new workout, set it as selected (requires ID update, usually reload does it)
            if (!formData.id && workouts.length > 0) {
                // The hook reloads workouts, selected will be set by the useEffect but let's be safe
                setSelectedWorkout(null);
            }
        }
    };

    const onDelete = async (id, title) => {
        const success = await handleDelete(id, title);
        if (success) {
            setSelectedWorkout(null);
            setIsDirty(false);
        }
    };

    const handleApplyTemplate = useCallback((setValues) => {
        setValues(templateWorkoutState(selectedDate));
    }, [selectedDate]);

    // PageLoader logic AFTER all hooks to follow Rules of Hooks
    if (isInitialLoad) {
        return <ContentLoader loading={true} message="Carregando treinos..." />;
    }

    if (error) {
        console.error("Workouts load error:", error);
    }

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Treinos" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="workouts-container mt-3">
            {error && <div className="alert alert-danger shadow-sm border-0">{error}</div>}

            <MasterDetailLayout
                master={
                    <WorkoutSidebar
                        workouts={workouts}
                        activeId={selectedWorkout?.id}
                        onSelect={handleSelectWorkout}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                        selectedDate={selectedDate}
                        onDateChange={handleDateChange}
                    />
                }
                detail={
                    <WorkoutEditor
                        selectedDate={selectedDate}
                        initialValues={selectedWorkout}
                        onSave={onSave}
                        isSaving={saving}
                        onDelete={onDelete}
                        onApplyTemplate={handleApplyTemplate}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default WorkoutsPage;
